import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as RegistraturaIcon,
  AccountBalance as GestiuneIcon,
  Event as ConcediiIcon,
  People as UtilizatoriIcon,
  Church as ParohiiIcon,
  Settings as AdministrareIcon,
  AccountCircle,
  Logout,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Work as PontajeIcon,
  Person as AngajatiIcon,
  Groups as ResurseUmaneIcon,
  // Administrative Icons
  Business as CancelarieIcon,
  Gavel as OficiulJuridicIcon,
  Book as OficiulCanonicIcon,
  Search as CorpulInspectieIcon,
  Group as ConsiliulEparhialIcon,
  BusinessCenter as SectorulAdministrativIcon,
  Church as SectorulPastoralIcon,
  School as SectorulCateheticIcon,
  Palette as SectorulCulturalIcon,
  AttachMoney as SectorulEconomicIcon,
  VolunteerActivism as SectorulSocialIcon,
  Campaign as SectorulMassMediaIcon,
  Architecture as SectorulPatrimoniuIcon,
  Brush as ComisiaCulturaIcon,
  LocationOn as DepartamentulCimitireIcon,
  Inventory as ComisiaInventarIcon,
  AccountBalance as AdministratiaControlIcon,
  Build as OficiulTehnicIcon,
  Church,
  Description as ContracteIcon,
  Business as BusinessIcon,
  Description,
  Inventory,
  SwapHoriz,
  Payment
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [administrareOpen, setAdministrareOpen] = useState(false);
  const [resurseUmaneOpen, setResurseUmaneOpen] = useState(false);
  const [structuraEparhialaOpen, setStructuraEparhialaOpen] = useState(false);
  const [sectorulPastoralOpen, setSectorulPastoralOpen] = useState(false);
  const [sectorulCateheticOpen, setSectorulCateheticOpen] = useState(false);
  const [sectorulCulturalOpen, setSectorulCulturalOpen] = useState(false);
  const [sectorulSocialOpen, setSectorulSocialOpen] = useState(false);
  const [sectorulMassMediaOpen, setSectorulMassMediaOpen] = useState(false);
  const [gestiuneOpen, setGestiuneOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  const handleAdministrareToggle = () => {
    setAdministrareOpen(!administrareOpen);
  };

  const handleResurseUmaneToggle = () => {
    setResurseUmaneOpen(!resurseUmaneOpen);
  };

  const handleStructuraEparhialaToggle = () => {
    setStructuraEparhialaOpen(!structuraEparhialaOpen);
  };

  const handleSectorulPastoralToggle = () => {
    setSectorulPastoralOpen(!sectorulPastoralOpen);
  };

  const handleSectorulCateheticToggle = () => {
    setSectorulCateheticOpen(!sectorulCateheticOpen);
  };

  const handleSectorulCulturalToggle = () => {
    setSectorulCulturalOpen(!sectorulCulturalOpen);
  };

  const handleSectorulSocialToggle = () => {
    setSectorulSocialOpen(!sectorulSocialOpen);
  };

  const handleSectorulMassMediaToggle = () => {
    setSectorulMassMediaOpen(!sectorulMassMediaOpen);
  };

  const handleGestiuneToggle = () => {
    setGestiuneOpen(!gestiuneOpen);
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      permission: null
    },
    {
      text: 'Registratura',
      icon: <RegistraturaIcon />,
      path: '/registratura',
      permission: 'registratura.read'
    },
    {
      text: 'Gestiune',
      icon: <GestiuneIcon />,
      path: '/gestiune',
      permission: 'gestiune.read',
      hasSubmenu: true
    },
    {
      text: 'Contracte',
      icon: <ContracteIcon />,
      path: '/contracte',
      permission: 'contracte.read'
    }
  ];

  const resurseUmaneItems = [
    {
      text: 'Concedii',
      icon: <ConcediiIcon />,
      path: '/concedii',
      permission: 'concedii.read'
    },
    {
      text: 'Pontaje',
      icon: <PontajeIcon />,
      path: '/pontaje',
      permission: 'pontaje.read'
    },
    {
      text: 'Angajati',
      icon: <AngajatiIcon />,
      path: '/angajati',
      permission: 'angajati.read'
    }
  ];

  const administrareItems = [
    {
      text: 'Utilizatori',
      icon: <UtilizatoriIcon />,
      path: '/administrare-utilizatori',
      permission: 'utilizatori.read'
    },
    {
      text: 'Gestiuni',
      icon: <GestiuneIcon />,
      path: '/gestiuni',
      permission: 'gestiuni.read'
    },
    {
      text: 'Parohii',
      icon: <ParohiiIcon />,
      path: '/parohii',
      permission: 'parohii.read'
    }
  ];

  const gestiuneItems = [
    {
      text: 'Gestiune Principală',
      icon: <GestiuneIcon />,
      path: '/gestiune',
      permission: 'gestiune.read'
    },
    {
      text: 'Tranzacții',
      icon: <GestiuneIcon />,
      path: '/gestiune/tranzactii',
      permission: 'gestiune.read'
    },
    {
      text: 'Facturi Intrare',
      icon: <Description />,
      path: '/gestiune/facturi-intrare',
      permission: 'gestiune.read'
    },
    {
      text: 'Facturi Ieșire',
      icon: <Description />,
      path: '/gestiune/facturi-iesire',
      permission: 'gestiune.read'
    },
    {
      text: 'Devize Materiale',
      icon: <Inventory />,
      path: '/gestiune/devize-materiale',
      permission: 'gestiune.read'
    },
    {
      text: 'Bonuri Transfer',
      icon: <SwapHoriz />,
      path: '/gestiune/bonuri-transfer',
      permission: 'gestiune.read'
    },
    {
      text: 'Plăți',
      icon: <Payment />,
      path: '/gestiune/plati',
      permission: 'gestiune.read'
    },
    {
      text: 'Inventar',
      icon: <Inventory />,
      path: '/gestiune/inventar',
      permission: 'gestiune.read'
    },
    {
      text: 'Furnizori',
      icon: <BusinessIcon />,
      path: '/gestiune/furnizori',
      permission: 'gestiune.read'
    }
  ];

  const structuraEparhialaItems = [
    // Structura de bază
    {
      text: 'Cancelarie',
      icon: <CancelarieIcon />,
      path: '/cancelarie',
      permission: 'cancelarie.read',
      hasSubmenu: false
    },
    {
      text: 'Oficiul Juridic',
      icon: <OficiulJuridicIcon />,
      path: '/oficiul-juridic',
      permission: 'oficiul-juridic.read',
      hasSubmenu: false
    },
    {
      text: 'Oficiul Canonic',
      icon: <OficiulCanonicIcon />,
      path: '/oficiul-canonic',
      permission: 'oficiul-canonic.read',
      hasSubmenu: false
    },
    {
      text: 'Corpul de Inspecție',
      icon: <CorpulInspectieIcon />,
      path: '/corpul-inspectie',
      permission: 'corpul-inspectie.read',
      hasSubmenu: false
    },
    {
      text: 'Consiliul Eparhial',
      icon: <ConsiliulEparhialIcon />,
      path: '/consiliul-eparhial',
      permission: 'consiliul-eparhial.read',
      hasSubmenu: false
    },
    // Sectoare administrative
    {
      text: 'Sectorul Administrativ-Bisericesc',
      icon: <SectorulAdministrativIcon />,
      path: '/sectorul-administrativ-bisericesc',
      permission: 'sectorul-administrativ.read',
      hasSubmenu: false
    },
    {
      text: 'Sectorul Pastoral-Liturgic',
      icon: <SectorulPastoralIcon />,
      path: '/sectorul-pastoral-liturgic',
      permission: 'sectorul-pastoral.read',
      hasSubmenu: true,
      submenuItems: [
        {
          text: 'Departamentul Pastoral-Liturgic',
          path: '/sectorul-pastoral-liturgic?tab=0',
          permission: 'sectorul-pastoral.read'
        },
        {
          text: 'Departamentul pentru Misiune',
          path: '/sectorul-pastoral-liturgic?tab=0',
          permission: 'sectorul-pastoral.read'
        }
      ]
    },
    {
      text: 'Sectorul Catehetic-Educațional',
      icon: <SectorulCateheticIcon />,
      path: '/sectorul-catehetic-educational',
      permission: 'sectorul-catehetic.read',
      hasSubmenu: true,
      submenuItems: [
        {
          text: 'Departamentul Catehetic',
          path: '/sectorul-catehetic-educational?tab=0',
          permission: 'sectorul-catehetic.read'
        },
        {
          text: 'Departamentul Educațional',
          path: '/sectorul-catehetic-educational?tab=0',
          permission: 'sectorul-catehetic.read'
        },
        {
          text: 'Centrul de Studii Teologice',
          path: '/sectorul-catehetic-educational?tab=0',
          permission: 'sectorul-catehetic.read'
        },
        {
          text: 'Uniunea de Formare Continuă',
          path: '/sectorul-catehetic-educational?tab=0',
          permission: 'sectorul-catehetic.read'
        }
      ]
    },
    {
      text: 'Sectorul Cultural, Tineret și Editorial',
      icon: <SectorulCulturalIcon />,
      path: '/sectorul-cultural-tineret-editorial',
      permission: 'sectorul-cultural.read',
      hasSubmenu: true,
      submenuItems: [
        {
          text: 'Departamentul Cultural',
          path: '/sectorul-cultural-tineret-editorial?tab=0',
          permission: 'sectorul-cultural.read'
        },
        {
          text: 'Departamentul pentru Tineret',
          path: '/sectorul-cultural-tineret-editorial?tab=0',
          permission: 'sectorul-cultural.read'
        },
        {
          text: 'Departamentul Editorial',
          path: '/sectorul-cultural-tineret-editorial?tab=0',
          permission: 'sectorul-cultural.read'
        }
      ]
    },
    {
      text: 'Sectorul Economic',
      icon: <SectorulEconomicIcon />,
      path: '/sectorul-economic',
      permission: 'sectorul-economic.read',
      hasSubmenu: false
    },
    {
      text: 'Sectorul Social-Filantropic',
      icon: <SectorulSocialIcon />,
      path: '/sectorul-social-filantropic',
      permission: 'sectorul-social.read',
      hasSubmenu: true,
      submenuItems: [
        {
          text: 'Departamentul Social',
          path: '/sectorul-social-filantropic?tab=0',
          permission: 'sectorul-social.read'
        },
        {
          text: 'Departamentul pentru Familie',
          path: '/sectorul-social-filantropic?tab=0',
          permission: 'sectorul-social.read'
        },
        {
          text: 'Departamentul pentru Preoți și Diaconi',
          path: '/sectorul-social-filantropic?tab=0',
          permission: 'sectorul-social.read'
        },
        {
          text: 'Departamentul pentru Bătrâni',
          path: '/sectorul-social-filantropic?tab=0',
          permission: 'sectorul-social.read'
        },
        {
          text: 'Departamentul pentru Bolnavi',
          path: '/sectorul-social-filantropic?tab=0',
          permission: 'sectorul-social.read'
        },
        {
          text: 'Departamentul pentru Integrarea Imigranților',
          path: '/sectorul-social-filantropic?tab=0',
          permission: 'sectorul-social.read'
        }
      ]
    },
    {
      text: 'Sectorul Mass-Media',
      icon: <SectorulMassMediaIcon />,
      path: '/sectorul-mass-media',
      permission: 'sectorul-mass-media.read',
      hasSubmenu: true,
      submenuItems: [
        {
          text: 'Departamentul Mass-Media și de Informare',
          path: '/sectorul-mass-media?tab=0',
          permission: 'sectorul-mass-media.read'
        },
        {
          text: 'Departamentul pentru Comunicare Internă',
          path: '/sectorul-mass-media?tab=0',
          permission: 'sectorul-mass-media.read'
        },
        {
          text: 'Departamentul pentru Tehnologie și Instrumente Mass-Media',
          path: '/sectorul-mass-media?tab=0',
          permission: 'sectorul-mass-media.read'
        },
        {
          text: 'Departamentul pentru Dialog Interconfesional',
          path: '/sectorul-mass-media?tab=0',
          permission: 'sectorul-mass-media.read'
        },
        {
          text: 'Departamentul pentru Relații Publice',
          path: '/sectorul-mass-media?tab=0',
          permission: 'sectorul-mass-media.read'
        },
        {
          text: 'Departamentul pentru Presă',
          path: '/sectorul-mass-media?tab=0',
          permission: 'sectorul-mass-media.read'
        }
      ]
    },
    {
      text: 'Sectorul Patrimoniu',
      icon: <SectorulPatrimoniuIcon />,
      path: '/sectorul-patrimoniu',
      permission: 'sectorul-patrimoniu.read',
      hasSubmenu: false
    },
    // Comisii și departamente
    {
      text: 'Comisia de Cultură',
      icon: <ComisiaCulturaIcon />,
      path: '/comisia-de-cultura',
      permission: 'comisia-cultura.read',
      hasSubmenu: false
    },
    {
      text: 'Departamentul Cimitire',
      icon: <DepartamentulCimitireIcon />,
      path: '/departamentul-cimitire',
      permission: 'departamentul-cimitire.read',
      hasSubmenu: false
    },
    {
      text: 'Comisia de Inventar',
      icon: <ComisiaInventarIcon />,
      path: '/comisia-de-inventar',
      permission: 'comisia-inventar.read',
      hasSubmenu: false
    },
    {
      text: 'Administrația Control Financiar',
      icon: <AdministratiaControlIcon />,
      path: '/administratia-control-financiar',
      permission: 'administratia-control.read',
      hasSubmenu: false
    },
    {
      text: 'Oficiul Tehnic',
      icon: <OficiulTehnicIcon />,
      path: '/oficiul-tehnic',
      permission: 'oficiul-tehnic.read',
      hasSubmenu: false
    }
  ];

  const filteredMenuItems = menuItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const filteredResurseUmaneItems = resurseUmaneItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const filteredAdministrareItems = administrareItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const filteredGestiuneItems = gestiuneItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const filteredStructuraEparhialaItems = structuraEparhialaItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const hasResurseUmaneAccess = filteredResurseUmaneItems.length > 0;
  const hasAdministrareAccess = filteredAdministrareItems.length > 0;
  const hasGestiuneAccess = filteredGestiuneItems.length > 0;
  const hasStructuraEparhialaAccess = filteredStructuraEparhialaItems.length > 0;

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ color: 'primary.main' }}>
          Theofania
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  if (item.hasSubmenu) {
                    if (item.text === 'Gestiune') {
                      handleGestiuneToggle();
                    }
                  } else {
                    navigate(item.path);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }
                }}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
                {item.hasSubmenu && item.text === 'Gestiune' && (
                  gestiuneOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </ListItemButton>
            </ListItem>
            
            {/* Submeniu Gestiune */}
            {item.text === 'Gestiune' && gestiuneOpen && hasGestiuneAccess && (
              <List component="div" disablePadding>
                {filteredGestiuneItems.map((subItem) => (
                  <ListItem key={subItem.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
        
        {/* Meniu Resurse Umane */}
        {hasResurseUmaneAccess && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={handleResurseUmaneToggle}>
                <ListItemIcon>
                  <ResurseUmaneIcon />
                </ListItemIcon>
                <ListItemText primary="Resurse Umane" />
                {resurseUmaneOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            {resurseUmaneOpen && (
              <List component="div" disablePadding>
                {filteredResurseUmaneItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                      onClick={() => {
                        navigate(item.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
        
        {/* Meniu Administrare */}
        {hasAdministrareAccess && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={handleAdministrareToggle}>
                <ListItemIcon>
                  <AdministrareIcon />
                </ListItemIcon>
                <ListItemText primary="Administrare" />
                {administrareOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            {administrareOpen && (
              <List component="div" disablePadding>
                {filteredAdministrareItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                      onClick={() => {
                        navigate(item.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
        
        {/* Meniu Structura Eparhială */}
        {hasStructuraEparhialaAccess && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={handleStructuraEparhialaToggle}>
                <ListItemIcon>
                  <Church />
                </ListItemIcon>
                <ListItemText primary="Structura Eparhială" />
                {structuraEparhialaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            {structuraEparhialaOpen && (
              <List component="div" disablePadding>
                {filteredStructuraEparhialaItems.map((item) => (
                  <React.Fragment key={item.text}>
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === item.path}
                        onClick={() => {
                          if (item.hasSubmenu) {
                            // Handle submenu toggle based on item type
                            if (item.text.includes('Pastoral-Liturgic')) {
                              handleSectorulPastoralToggle();
                            } else if (item.text.includes('Catehetic-Educațional')) {
                              handleSectorulCateheticToggle();
                            } else if (item.text.includes('Cultural, Tineret')) {
                              handleSectorulCulturalToggle();
                            } else if (item.text.includes('Social-Filantropic')) {
                              handleSectorulSocialToggle();
                            } else if (item.text.includes('Mass-Media')) {
                              handleSectorulMassMediaToggle();
                            }
                          } else {
                            navigate(item.path);
                            if (isMobile) {
                              setMobileOpen(false);
                            }
                          }
                        }}
                      >
                        <ListItemIcon>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                        {item.hasSubmenu && (
                          item.text.includes('Pastoral-Liturgic') ? (sectorulPastoralOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />) :
                          item.text.includes('Catehetic-Educațional') ? (sectorulCateheticOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />) :
                          item.text.includes('Cultural, Tineret') ? (sectorulCulturalOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />) :
                          item.text.includes('Social-Filantropic') ? (sectorulSocialOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />) :
                          item.text.includes('Mass-Media') ? (sectorulMassMediaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />) :
                          null
                        )}
                      </ListItemButton>
                    </ListItem>
                    
                    {/* Render submenu for each sector */}
                    {item.hasSubmenu && item.submenuItems && (
                      <>
                        {item.text.includes('Pastoral-Liturgic') && sectorulPastoralOpen && (
                          <List component="div" disablePadding>
                            {item.submenuItems.map((subItem) => (
                              <ListItem key={subItem.text} disablePadding>
                                <ListItemButton
                                  sx={{ pl: 8 }}
                                  selected={location.pathname === subItem.path}
                                  onClick={() => {
                                    navigate(subItem.path);
                                    if (isMobile) {
                                      setMobileOpen(false);
                                    }
                                  }}
                                >
                                  <ListItemText primary={subItem.text} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        )}
                        
                        {item.text.includes('Catehetic-Educațional') && sectorulCateheticOpen && (
                          <List component="div" disablePadding>
                            {item.submenuItems.map((subItem) => (
                              <ListItem key={subItem.text} disablePadding>
                                <ListItemButton
                                  sx={{ pl: 8 }}
                                  selected={location.pathname === subItem.path}
                                  onClick={() => {
                                    navigate(subItem.path);
                                    if (isMobile) {
                                      setMobileOpen(false);
                                    }
                                  }}
                                >
                                  <ListItemText primary={subItem.text} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        )}
                        
                        {item.text.includes('Cultural, Tineret') && sectorulCulturalOpen && (
                          <List component="div" disablePadding>
                            {item.submenuItems.map((subItem) => (
                              <ListItem key={subItem.text} disablePadding>
                                <ListItemButton
                                  sx={{ pl: 8 }}
                                  selected={location.pathname === subItem.path}
                                  onClick={() => {
                                    navigate(subItem.path);
                                    if (isMobile) {
                                      setMobileOpen(false);
                                    }
                                  }}
                                >
                                  <ListItemText primary={subItem.text} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        )}
                        
                        {item.text.includes('Social-Filantropic') && sectorulSocialOpen && (
                          <List component="div" disablePadding>
                            {item.submenuItems.map((subItem) => (
                              <ListItem key={subItem.text} disablePadding>
                                <ListItemButton
                                  sx={{ pl: 8 }}
                                  selected={location.pathname === subItem.path}
                                  onClick={() => {
                                    navigate(subItem.path);
                                    if (isMobile) {
                                      setMobileOpen(false);
                                    }
                                  }}
                                >
                                  <ListItemText primary={subItem.text} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        )}
                        
                        {item.text.includes('Mass-Media') && sectorulMassMediaOpen && (
                          <List component="div" disablePadding>
                            {item.submenuItems.map((subItem) => (
                              <ListItem key={subItem.text} disablePadding>
                                <ListItemButton
                                  sx={{ pl: 8 }}
                                  selected={location.pathname === subItem.path}
                                  onClick={() => {
                                    navigate(subItem.path);
                                    if (isMobile) {
                                      setMobileOpen(false);
                                    }
                                  }}
                                >
                                  <ListItemText primary={subItem.text} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}
          </>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Administrare Episcopie
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              <AccountCircle />
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.name}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.role}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Deconectare
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
